/**
 * Favorite-contact store.
 *
 * Persists a single favorite family member per signed-in user in localStorage
 * (key `madivladi:favorite-contact:<userId>`). The favorite powers the
 * one-tap "Покани" quick-invite in the user menu and the highlighted star in
 * the invite panel. Scoped per user id so each device/login keeps its own
 * choice; rehydrates whenever the active user id changes.
 *
 * @module features/p2p-chat/stores/favoriteContactStore
 */

import { create } from 'zustand';

const STORAGE_PREFIX = 'madivladi:favorite-contact:';

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function readFavorite(userId: string | null): string | null {
  if (!userId) return null;
  try {
    return globalThis.localStorage?.getItem(storageKey(userId)) ?? null;
  } catch {
    return null;
  }
}

function writeFavorite(userId: string, contactId: string | null): void {
  try {
    if (contactId === null) {
      globalThis.localStorage?.removeItem(storageKey(userId));
    } else {
      globalThis.localStorage?.setItem(storageKey(userId), contactId);
    }
  } catch {
    /* storage unavailable — keep in-memory value only */
  }
}

interface FavoriteContactState {
  /** The user whose favorite is currently loaded. */
  readonly userId: string | null;
  /** Roster id of the favorite contact, or null when none is set. */
  readonly favoriteId: string | null;
  /** Load the favorite for a user (no-op if already loaded for that user). */
  rehydrate: (userId: string | null) => void;
  /** Set (or toggle off) the favorite contact for the active user. */
  setFavorite: (contactId: string) => void;
  /** Clear the favorite contact for the active user. */
  clearFavorite: () => void;
}

export const useFavoriteContactStore = create<FavoriteContactState>(
  (set, get) => ({
    userId: null,
    favoriteId: null,

    rehydrate: (userId) => {
      if (get().userId === userId) return;
      set({ userId, favoriteId: readFavorite(userId) });
    },

    setFavorite: (contactId) => {
      const { userId, favoriteId } = get();
      if (!userId) return;
      // Toggling the current favorite clears it.
      const next = favoriteId === contactId ? null : contactId;
      writeFavorite(userId, next);
      set({ favoriteId: next });
    },

    clearFavorite: () => {
      const { userId } = get();
      if (!userId) return;
      writeFavorite(userId, null);
      set({ favoriteId: null });
    },
  })
);
